import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useDeposit } from "@/context/DepositContext";
import { useNotificationContext } from "@/context/NotificationContext";
import { useAlert } from "@/hooks/useAlert";
import { PageHeader, StatsCard } from "@/components/common";
import { DepositEditModal, QuotesSection, UpcomingJobsSection, NotificationsSection, MessagesSection, } from "./components";
import { notificationsService } from "@/api/services/notifications.service";
import { messagesService, } from "@/api/services/messages.service";
import { jobsService } from "@/api/services/jobs.service";
import { quotesService } from "@/api/services/quotes.service";
import { invoicesService } from "@/api/services/invoices.service";
import { formatJobAddress } from "@/utils/address";
import { formatDateOnlyDisplay, getTimeFromIso } from "@/utils/dateTime";
import { getUserDisplayName } from "@/utils/formatDisplayName";
import { formatCurrency } from "@/utils/formatters";
import styles from "./AdminHome.module.css";
import statsCardStyles from "@/components/common/StatsCard/StatsCard.module.css";
// Icons for stats cards
const DocumentIcon = () => (_jsx("svg", { width: "28", height: "28", viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: _jsx("path", { d: "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z", stroke: "#07485F", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" }) }));
const BusinessDashboardIcon = () => (_jsxs("svg", { width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: [_jsx("rect", { x: "3", y: "7", width: "18", height: "12", rx: "2", stroke: "#07485F", strokeWidth: "2" }), _jsx("path", { d: "M9 7V5a2 2 0 012-2h2a2 2 0 012 2v2", stroke: "#07485F", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" })] }));
const ProjectDashboardIcon = () => (_jsxs("svg", { width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: [_jsx("rect", { x: "3", y: "4", width: "18", height: "16", rx: "2", stroke: "#07485F", strokeWidth: "2" }), _jsx("path", { d: "M3 9h18M9 4v16", stroke: "#07485F", strokeWidth: "2", strokeLinecap: "round" })] }));
const SalesDashboardIcon = () => (_jsxs("svg", { width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: [_jsx("path", { d: "M5 19v-6M12 19V5M19 19v-9", stroke: "#07485F", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" }), _jsx("path", { d: "M4 19h16", stroke: "#07485F", strokeWidth: "2", strokeLinecap: "round" })] }));
function mapQuoteToInProgressDisplay(q) {
    return {
        id: q.id,
        title: q.title,
        startedDate: formatDateOnlyDisplay(q.createdAt),
        clientName: q.companyName ?? q.clientName ?? null,
    };
}
function mapJobToUpcomingJobDisplay(job) {
    const duration = job.durationDays != null && job.durationDays > 0
        ? `${job.durationDays} Day${job.durationDays !== 1 ? "s" : ""}`
        : "TBD";
    const timeRange = getTimeFromIso(job.commencementDate) || "TBD";
    return {
        id: job.id,
        quoteId: job.quoteId ?? job.id,
        linkedJobId: job.id,
        jobNumber: job.jobNumber,
        location: formatJobAddress(job, "TBD"),
        date: formatDateOnlyDisplay(job.commencementDate),
        timeRange,
        duration,
        imageUrl: job.imageUrl ?? undefined,
        status: job.status,
    };
}
export const AdminHome = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { confirm } = useAlert();
    const { loading: depositLoading, defaultDepositPercentage, updateDeposit, } = useDeposit();
    const [depositModalOpen, setDepositModalOpen] = useState(false);
    const { latestNotification, clearLatestNotification } = useNotificationContext();
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [notificationsTotalCount, setNotificationsTotalCount] = useState(0);
    const [notificationsLoadingMore, setNotificationsLoadingMore] = useState(false);
    const [quotesAwaitingCount, setQuotesAwaitingCount] = useState(null);
    const [unpaidInvoicesSum, setUnpaidInvoicesSum] = useState(null);
    const [notificationsLoading, setNotificationsLoading] = useState(true);
    const [quotesInProgress, setQuotesInProgress] = useState([]);
    const [upcomingJobs, setUpcomingJobs] = useState([]);
    const [dashboardLoading, setDashboardLoading] = useState(true);
    const [messagesData, setMessagesData] = useState(null);
    const [messagesLoading, setMessagesLoading] = useState(true);
    const fetchDashboard = useCallback(async () => {
        setDashboardLoading(true);
        try {
            const [awaitingRes, unpaidRes, inProgressRes, jobsRes] = await Promise.all([
                quotesService.getQuotesAwaitingApproval(),
                invoicesService.getUnpaidInvoices(),
                quotesService.getQuotesInProgress(),
                jobsService.getUpcomingJobs(),
            ]);
            setQuotesAwaitingCount(awaitingRes.data.length);
            const sum = (unpaidRes.data.data ?? []).reduce((acc, inv) => acc + (inv.total ?? 0), 0);
            setUnpaidInvoicesSum(sum);
            setQuotesInProgress((inProgressRes.data ?? []).map(mapQuoteToInProgressDisplay));
            const jobs = jobsRes.data?.data ?? [];
            const sorted = [...jobs].sort((a, b) => new Date(a.commencementDate ?? 0).getTime() -
                new Date(b.commencementDate ?? 0).getTime());
            setUpcomingJobs(sorted.map(mapJobToUpcomingJobDisplay));
        }
        catch {
            setQuotesAwaitingCount(0);
            setUnpaidInvoicesSum(0);
            setQuotesInProgress([]);
            setUpcomingJobs([]);
        }
        finally {
            setDashboardLoading(false);
        }
    }, []);
    useEffect(() => {
        fetchDashboard();
    }, [fetchDashboard]);
    const NOTIFICATIONS_PAGE_SIZE = 20;
    const fetchNotifications = useCallback(async () => {
        setNotificationsLoading(true);
        try {
            const res = await notificationsService.getMyNotifications({
                unreadOnly: true,
                limit: NOTIFICATIONS_PAGE_SIZE,
                offset: 0,
                roles: ["STAFF"],
            });
            setNotifications(res.data.data ?? []);
            setUnreadCount(res.data.unreadCount ?? 0);
            setNotificationsTotalCount(res.data.totalCount ?? 0);
        }
        catch {
            setNotifications([]);
            setUnreadCount(0);
            setNotificationsTotalCount(0);
        }
        finally {
            setNotificationsLoading(false);
        }
    }, []);
    const handleLoadMoreNotifications = useCallback(async () => {
        if (notificationsLoadingMore ||
            notifications.length >= notificationsTotalCount)
            return;
        setNotificationsLoadingMore(true);
        try {
            const res = await notificationsService.getMyNotifications({
                unreadOnly: true,
                limit: NOTIFICATIONS_PAGE_SIZE,
                offset: notifications.length,
                roles: ["STAFF"],
            });
            const newItems = res.data.data ?? [];
            setNotifications((prev) => [...prev, ...newItems]);
        }
        finally {
            setNotificationsLoadingMore(false);
        }
    }, [notificationsLoadingMore, notifications.length, notificationsTotalCount]);
    useEffect(() => {
        fetchNotifications();
    }, [fetchNotifications]);
    const fetchMessages = useCallback(async () => {
        setMessagesLoading(true);
        try {
            const data = await messagesService.getStaffHomeLatestMessages(3);
            setMessagesData(data);
        }
        catch {
            setMessagesData({ general: [], quote: [], job: [] });
        }
        finally {
            setMessagesLoading(false);
        }
    }, []);
    useEffect(() => {
        fetchMessages();
    }, [fetchMessages]);
    const location = useLocation();
    const basePath = location.pathname.startsWith("/staff") ? "/staff" : "/admin";
    const handleMessageThreadClick = useCallback((thread) => {
        if (thread.type === "GENERAL") {
            navigate(`${basePath}/messages`, {
                state: {
                    contactId: thread.contactId,
                    contactType: thread.contactType,
                },
            });
        }
        else if (thread.type === "QUOTE" && thread.quoteId) {
            navigate(`${basePath}/estimating/${thread.quoteId}/edit`, {
                state: { tab: "notifications", contactId: thread.contactId },
            });
        }
        else if (thread.type === "JOB" && thread.jobId) {
            navigate(`${basePath}/jobs/${thread.jobId}/edit`, {
                state: { tab: "notifications", contactId: thread.contactId },
            });
        }
    }, [navigate, basePath]);
    useEffect(() => {
        if (!latestNotification)
            return;
        setNotifications((prev) => [latestNotification, ...prev]);
        setUnreadCount((prev) => prev + 1);
        clearLatestNotification();
    }, [latestNotification, clearLatestNotification]);
    const handleViewJob = useCallback(({ quoteId, linkedJobId, }) => {
        if (linkedJobId) {
            navigate(`${basePath}/jobs/${linkedJobId}/edit`);
        }
        else {
            navigate(`${basePath}/estimating/${quoteId}/edit`);
        }
    }, [navigate, basePath]);
    const handleQuoteClick = useCallback((quoteId) => {
        navigate(`${basePath}/estimating/${quoteId}/edit`);
    }, [navigate, basePath]);
    const handleNotificationClick = useCallback(async (notification) => {
        const confirmed = await confirm("Are you sure you want to close this notification as it will disappear?", "Confirm");
        if (!confirmed)
            return;
        const id = notification.id;
        setNotifications((prev) => prev.filter((n) => n.id !== id));
        setUnreadCount((prev) => Math.max(0, prev - 1));
        try {
            await notificationsService.markAsRead(id, {
                actionedByName: getUserDisplayName(user, "Staff"),
            });
        }
        catch {
            fetchNotifications();
        }
    }, [confirm, fetchNotifications, user]);
    return (_jsxs("div", { className: styles.pageContent, children: [_jsx(PageHeader, { title: "Home", userName: user?.name || "User", userEmail: user?.email || "" }), _jsxs("div", { className: styles.homeGrid, children: [_jsx("div", { className: styles.statsRow, children: _jsxs("div", { className: styles.statsGrid, children: [_jsx(StatsCard, { icon: _jsx(DocumentIcon, {}), label: "QUOTES AWAITING APPROVAL", value: dashboardLoading ? "--" : String(quotesAwaitingCount ?? 0), variant: "teal" }), _jsx(StatsCard, { icon: _jsx(DocumentIcon, {}), label: "UNPAID INVOICES", value: dashboardLoading
                                        ? "$--"
                                        : formatCurrency(unpaidInvoicesSum ?? 0), variant: "teal" }), _jsx("div", { className: styles.depositCardClickable, role: "button", tabIndex: 0, onClick: () => setDepositModalOpen(true), onKeyDown: (e) => {
                                        if (e.key === "Enter" || e.key === " ") {
                                            e.preventDefault();
                                            setDepositModalOpen(true);
                                        }
                                    }, children: _jsx(StatsCard, { icon: _jsx(DocumentIcon, {}), label: "DEFAULT DEPOSIT %", value: depositLoading ? "--%" : `${defaultDepositPercentage}%`, variant: "teal" }) }), _jsx(StatsCard, { icon: _jsx(DocumentIcon, {}), label: "UNACTIONED NOTIFICATIONS", value: notificationsLoading ? "--" : String(unreadCount ?? 0), variant: "teal", className: statsCardStyles.cardBorderRadiusTopRight })] }) }), _jsx("div", { className: styles.mainRow, children: _jsxs("div", { className: styles.mainGrid, children: [_jsx("div", { className: styles.leftColumn, children: _jsx(QuotesSection, { quotes: quotesInProgress, onQuoteClick: handleQuoteClick }) }), _jsx("div", { className: styles.middleColumn, children: _jsx(UpcomingJobsSection, { jobs: upcomingJobs, onViewJob: handleViewJob }) })] }) }), _jsx("div", { className: styles.notificationsColumn, children: _jsx(NotificationsSection, { notifications: notifications, onNotificationClick: handleNotificationClick, fromAdminHome: true, onLoadMore: handleLoadMoreNotifications, hasMore: notifications.length < notificationsTotalCount, loadingMore: notificationsLoadingMore }) }), _jsxs("div", { className: styles.messagesSection, children: [_jsx("div", { className: styles.messagesHeader, children: _jsx("h2", { className: styles.messagesTitle, children: "MESSAGES" }) }), _jsx(MessagesSection, { general: messagesData?.general ?? [], quote: messagesData?.quote ?? [], job: messagesData?.job ?? [], loading: messagesLoading, onThreadClick: handleMessageThreadClick })] }), _jsxs("div", { className: styles.dashboardsRow, children: [_jsx("h2", { className: styles.dashboardsTitle, children: "DASHBOARDS" }), _jsxs("div", { className: styles.dashboardButtons, children: [_jsxs("a", { href: "https://app.powerbi.com/links/2_p01aofeX?ctid=2af5baa1-9e9e-45c3-8c78-2230940d558f&pbi_source=linkShare", className: styles.dashboardButton, "aria-label": "Business Dashboard", children: [_jsx("div", { className: styles.dashboardIcon, children: _jsx(BusinessDashboardIcon, {}) }), _jsx("span", { className: styles.dashboardLabel, children: "Business Dashboard" })] }), _jsxs("a", { href: "https://app.powerbi.com/links/EyIQCaNrtv?ctid=2af5baa1-9e9e-45c3-8c78-2230940d558f&pbi_source=linkShare", target: "_blank", rel: "noopener noreferrer", className: styles.dashboardButton, "aria-label": "Project Dashboard", children: [_jsx("div", { className: styles.dashboardIcon, children: _jsx(ProjectDashboardIcon, {}) }), _jsx("span", { className: styles.dashboardLabel, children: "Project Dashboard" })] }), _jsxs("a", { href: "#", className: styles.dashboardButton, "aria-label": "Sales & Marketing Dashboard", children: [_jsx("div", { className: styles.dashboardIcon, children: _jsx(SalesDashboardIcon, {}) }), _jsx("span", { className: styles.dashboardLabel, children: "Sales & Marketing Dashboard" })] })] })] })] }), _jsx(DepositEditModal, { isOpen: depositModalOpen, onClose: () => setDepositModalOpen(false), currentValue: defaultDepositPercentage, onSave: updateDeposit })] }));
};
